import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { fetchCookieConfig } from './services/cookieConfigService';
import { purgeLegacyOrgLocalKeys } from './utils/orgLocalStorage';
import Login from './pages/Login';
import Verify2FA from './pages/Verify2FA';
import Dashboard from './pages/Dashboard';
import AdminPanel from './pages/AdminPanel';
import BulkUpload from './pages/BulkUpload';
import API from './pages/API';
import Profile from './pages/Profile';
import FileUpload from './pages/FileUpload';
import FastIdReport from './pages/FastIdReport';
import FastIdReportDetail from './pages/FastIdReportDetail';
import FileDownload from './pages/FileDownload';
import Query from './pages/Query';
import Chat from './pages/Chat';
import Signals from './pages/Signals';
import Segments from './pages/Segments';
import SegmentEditor from './pages/SegmentEditor';
import TemplateEditor from './pages/TemplateEditor';
import APIUsage from './pages/APIUsage';
import OfferReport from './pages/OfferReport';
import OfferAudienceStudio from './pages/OfferAudienceStudio';
import DataDictionary from './pages/DataDictionary';
import OrganizationProperties from './pages/OrganizationProperties';
import Docs from './pages/Docs';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

function AppRoutes() {
  const { isAuthenticated, passwordResetRequired } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Log react-router route changes
  React.useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('[RR location]', { pathname: location.pathname, search: location.search, hash: location.hash, href: window.location.href });
  }, [location.pathname, location.search, location.hash]);

  // Redirect to profile if password reset is required and user tries to access other pages
  React.useEffect(() => {
    if (isAuthenticated && passwordResetRequired && location.pathname !== '/profile') {
      navigate('/profile', { replace: true });
    }
  }, [isAuthenticated, passwordResetRequired, location.pathname, navigate]);

  return (
    <Routes>
      <Route path="/login" element={
        isAuthenticated ? (passwordResetRequired ? <Navigate to="/profile" /> : <Navigate to="/dashboard" />) : <Login />
      } />
      <Route path="/verify-2fa" element={
        isAuthenticated ? (passwordResetRequired ? <Navigate to="/profile" /> : <Navigate to="/dashboard" />) : <Verify2FA />
      } />
      <Route path="/" element={
        isAuthenticated ? (passwordResetRequired ? <Navigate to="/profile" /> : <Navigate to="/dashboard" />) : <Navigate to="/login" />
      } />
      
      <Route path="/" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="intro" element={<Navigate to="/api-keys" replace />} />
        <Route path="api-keys" element={<API />} />
        <Route path="profile" element={<Profile />} />
        <Route path="file-upload" element={<FileUpload />} />
        <Route path="fastid-report" element={<FastIdReport />} />
        <Route path="fastid-report/:executionId" element={<FastIdReportDetail />} />
        <Route path="segments" element={<Segments />} />
        <Route path="segments/templates/new" element={<TemplateEditor />} />
        <Route path="segments/templates/:id" element={<TemplateEditor />} />
        <Route path="segments/new" element={<SegmentEditor />} />
        <Route path="segments/:id" element={<SegmentEditor />} />
        <Route path="file-download" element={<FileDownload />} />
        <Route path="query" element={<Query />} />
        <Route path="chat" element={<Chat />} />
        <Route path="signals" element={<Signals />} />
        <Route path="offer-report" element={<OfferReport />} />
        <Route path="offer-report/:offerId" element={<OfferAudienceStudio />} />
        <Route path="api-usage" element={<APIUsage />} />
        <Route path="organization-properties" element={<OrganizationProperties />} />
        <Route path="data-dictionary" element={<DataDictionary />} />
        <Route path="docs" element={<Docs />} />
        <Route path="bulk-upload" element={<BulkUpload />} />
        <Route path="admin" element={<AdminPanel />} />
      </Route>
    </Routes>
  );
}

function App() {
  useEffect(() => {
    fetchCookieConfig();
    purgeLegacyOrgLocalKeys();
  }, []);
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Router>
          <AppRoutes />
        </Router>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
